import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { format, subDays } from 'date-fns';
import PageHeader from '../components/PageHeader';
import analyticsService, {
  DashboardSnapshot,
  AnalyticsMetric,
  ReportVolumeMetric,
  AnatomyPrevalenceMetric,
  CptPrevalenceMetric,
  ProductivityMetric,
  TurnaroundTimeMetric,
  CohortDemographicsMetric,
} from '../services/analyticsService';

const DATE_RANGE_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom range' },
];

const MetricCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Card sx={{ height: '100%' }}>
    <CardHeader title={title} titleTypographyProps={{ variant: 'h6', fontWeight: 500 }} />
    <Divider />
    <CardContent>{children}</CardContent>
  </Card>
);

const ReportVolumeCard: React.FC<{ metric: ReportVolumeMetric }> = ({ metric }) => {
  const { data } = metric;
  const [expanded, setExpanded] = useState(false);
  const limit = 5;

  const sortedDaily = useMemo(
    () => [...data.daily].sort((a, b) => b.count - a.count),
    [data.daily]
  );
  const maxDaily = useMemo(() => {
    if (!sortedDaily.length) return 0;
    return Math.max(...sortedDaily.map((d) => d.count));
  }, [sortedDaily]);

  const showExpand = sortedDaily.length > limit;
  const displayed = expanded ? sortedDaily : sortedDaily.slice(0, limit);

  return (
    <MetricCard title="Report Volume">
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <StatBox label="Total" value={data.total} />
        <StatBox label="Finalized" value={data.finalized} />
        <StatBox label="Drafts" value={data.drafts} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Finalization rate: {Math.round(data.finalization_rate * 100)}%
      </Typography>
      {sortedDaily.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Top daily volume
          </Typography>
          {displayed.map((day) => (
            <Box key={day.date} sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">{format(new Date(day.date), 'MMM d')}</Typography>
                <Typography variant="body2">{day.count}</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={maxDaily ? (day.count / maxDaily) * 100 : 0}
                sx={{ height: 6, borderRadius: 1 }}
              />
            </Box>
          ))}
          {showExpand && (
            <Box sx={{ mt: 1, textAlign: 'center' }}>
              <Typography
                variant="body2"
                color="primary.main"
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? 'Show top 5' : `Show all ${sortedDaily.length} days`}
              </Typography>
            </Box>
          )}
        </>
      )}
    </MetricCard>
  );
};

const StatBox: React.FC<{ label: string; value: number | string }> = ({ label, value }) => (
  <Box sx={{ flex: 1, textAlign: 'center', p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
    <Typography variant="h4" color="primary.main">
      {value}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const ExpandableTable: React.FC<{ rows: { label: string; count: number }[]; total: number; limit?: number }> = ({
  rows,
  total,
  limit = 5,
}) => {
  const [expanded, setExpanded] = useState(false);
  const showExpand = rows.length > limit;
  const displayed = expanded ? rows : rows.slice(0, limit);

  return (
    <>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell align="right">Count</TableCell>
              <TableCell>Share</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayed.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell>{row.label}</TableCell>
                <TableCell align="right">{row.count}</TableCell>
                <TableCell sx={{ width: '40%' }}>
                  <LinearProgress
                    variant="determinate"
                    value={total ? (row.count / total) * 100 : 0}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {showExpand && (
        <Box sx={{ mt: 1, textAlign: 'center' }}>
          <Typography
            variant="body2"
            color="primary.main"
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Show top 5' : `Show all ${rows.length}`}
          </Typography>
        </Box>
      )}
    </>
  );
};

const AnatomyPrevalenceCard: React.FC<{ metric: AnatomyPrevalenceMetric }> = ({ metric }) => {
  const rows = useMemo(
    () =>
      metric.data.by_anatomy.map((item) => ({
        label: `${item.body_part} (${item.modality})`,
        count: item.count,
      })),
    [metric.data.by_anatomy]
  );
  return (
    <MetricCard title="Anatomy Prevalence">
      <ExpandableTable rows={rows} total={metric.data.total} />
    </MetricCard>
  );
};

const CptPrevalenceCard: React.FC<{ metric: CptPrevalenceMetric }> = ({ metric }) => {
  const rows = useMemo(
    () =>
      metric.data.by_cpt.map((item) => ({
        label: `${item.cpt_code} — ${item.exam_name}`,
        count: item.count,
      })),
    [metric.data.by_cpt]
  );
  return (
    <MetricCard title="CPT Prevalence">
      <ExpandableTable rows={rows} total={metric.data.total} />
    </MetricCard>
  );
};

const ProductivityCard: React.FC<{ metric: ProductivityMetric }> = ({ metric }) => {
  const [expanded, setExpanded] = useState(false);
  const limit = 5;
  const rows = metric.data.radiologists;
  const showExpand = rows.length > limit;
  const displayed = expanded ? rows : rows.slice(0, limit);

  return (
    <MetricCard title="Radiologist Productivity">
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Radiologist</TableCell>
              <TableCell align="right">Reports</TableCell>
              <TableCell align="right">Transcriptions</TableCell>
              <TableCell align="right">Dictation minutes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayed.map((rad) => (
              <TableRow key={rad.user_id}>
                <TableCell>{rad.username}</TableCell>
                <TableCell align="right">{rad.reports_created}</TableCell>
                <TableCell align="right">{rad.transcriptions}</TableCell>
                <TableCell align="right">{rad.transcription_minutes}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {showExpand && (
        <Box sx={{ mt: 1, textAlign: 'center' }}>
          <Typography
            variant="body2"
            color="primary.main"
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Show top 5' : `Show all ${rows.length}`}
          </Typography>
        </Box>
      )}
    </MetricCard>
  );
};

const TurnaroundTimeCard: React.FC<{ metric: TurnaroundTimeMetric }> = ({ metric }) => {
  const formatSeconds = (minutes: number) => (minutes * 60).toFixed(1);
  return (
    <MetricCard title="Turnaround Time">
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <StatBox label="Reports finalized" value={metric.data.count} />
        <StatBox label="Average (sec)" value={formatSeconds(metric.data.average_minutes)} />
        <StatBox label="Median (sec)" value={formatSeconds(metric.data.median_minutes)} />
        <StatBox label="P95 (sec)" value={formatSeconds(metric.data.p95_minutes)} />
      </Box>
    </MetricCard>
  );
};

const CohortDemographicsCard: React.FC<{ metric: CohortDemographicsMetric }> = ({ metric }) => {
  const total = metric.data.total_patients;
  return (
    <MetricCard title="Cohort Demographics">
      <StatBox label="Total patients" value={total} />
      <Box sx={{ mt: 2 }}>
        {metric.data.by_gender.map((item) => (
          <Box key={item.gender} sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                {item.gender}
              </Typography>
              <Typography variant="body2">{item.count}</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={total ? (item.count / total) * 100 : 0}
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>
        ))}
      </Box>
    </MetricCard>
  );
};

const MetricRenderer: React.FC<{ metric: AnalyticsMetric }> = ({ metric }) => {
  switch (metric.metric) {
    case 'report_volume':
      return <ReportVolumeCard metric={metric} />;
    case 'anatomy_prevalence':
      return <AnatomyPrevalenceCard metric={metric} />;
    case 'cpt_prevalence':
      return <CptPrevalenceCard metric={metric} />;
    case 'productivity':
      return <ProductivityCard metric={metric} />;
    case 'turnaround_time':
      return <TurnaroundTimeCard metric={metric} />;
    case 'cohort_demographics':
      return <CohortDemographicsCard metric={metric} />;
    default:
      return null;
  }
};

const AnalyticsDashboard: React.FC = () => {
  const [snapshot, setSnapshot] = useState<DashboardSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState('30');
  const [startDate, setStartDate] = useState<string>(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await analyticsService.getDashboardSnapshot({
        start_date: startDate,
        end_date: endDate,
      });
      setSnapshot(data);
    } catch (err) {
      console.error('Failed to load analytics dashboard:', err);
      setError('Failed to load analytics dashboard. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const handleRangeChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setRange(value);
    if (value !== 'custom') {
      const days = parseInt(value, 10);
      setStartDate(format(subDays(new Date(), days), 'yyyy-MM-dd'));
      setEndDate(format(new Date(), 'yyyy-MM-dd'));
    }
  };

  return (
    <Box>
      <PageHeader title="Analytics Dashboard" subtitle="Organization-wide radiology intelligence" />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel id="date-range-label">Date range</InputLabel>
              <Select
                labelId="date-range-label"
                value={range}
                label="Date range"
                onChange={handleRangeChange}
              >
                {DATE_RANGE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Start date"
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setRange('custom');
              }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End date"
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setRange('custom');
              }}
              InputLabelProps={{ shrink: true }}
            />
            {loading && <CircularProgress size={24} thickness={4} />}
            {snapshot && (
              <Typography variant="body2" color="text.secondary">
                Generated: {format(new Date(snapshot.generated_at), 'MMM d, yyyy h:mm a')}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {snapshot && !loading && (
        <Grid container spacing={3}>
          {snapshot.metrics.map((metric) => (
            <Grid item xs={12} md={6} lg={4} key={metric.metric}>
              <MetricRenderer metric={metric} />
            </Grid>
          ))}
        </Grid>
      )}

      {!snapshot && !loading && !error && (
        <Typography color="text.secondary">No analytics data available.</Typography>
      )}
    </Box>
  );
};

export default AnalyticsDashboard;
